import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { QnA, QuestionUploadedFile } from "../../modals/qna/qna.interface";
import { environment } from "../../../../environments/environment";
import { ChatBotStyleAndConfig } from "../../modals/style-and-config/style-config";
import { ConfigService } from "../../../common/services/config/config.service";
import { RuntimeConfigService } from "../../../common/services/runtime-config/runtime-config.service";
import { SessionService } from "../../services/app-state-services/session/session.service";
import { ResponseStreamService } from "../../services/response-stream/response-stream.service";
import { VoiceRecognitionService } from "../../services/voice-recognition/voice-recognition.service";
import { TextToSpeechService } from "../../services/text-to-speech/text-to-speech.service";
import { ChatStateService } from "../../services/app-state-services/chat-main/chat-state.service";
import { firstValueFrom, Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ChatService, ChannelAvailability } from "../../services/chat/chat.service";
import { ComponentInteractionService } from "../../services/chat/component-interaction.service";
import {
  ConversationAttachmentService,
  ConversationAttachmentResponse,
} from "../../services/chat/conversation-attachment.service";
import { HttpErrorResponse } from "@angular/common/http";
import { RSocketService } from "../../services/rsocket-service/r-socket.service";
import { RealtimeServiceFactory } from "../../services/factories/realtime-service.factory";
import { IRealtimeService } from "../../services/interfaces/realtime-service.interface";

@Component({
  selector: "app-chat-bubble",
  templateUrl: "./chat-bubble.component.html",
  styleUrl: "./chat-bubble.component.scss",
  standalone: false
})
export class ChatBubbleComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild("modalBody") modalBody!: ElementRef;
  @ViewChildren('userQnaElement') userQnaElements!: QueryList<ElementRef>;
  @ViewChild('textAreaRef') textArea!: ElementRef;
  @ViewChildren("messageContainer") messageContainers!: QueryList<ElementRef>;
  @Output() closeModalEvent = new EventEmitter<boolean>(); 
  @ViewChild('pickerWrapper') pickerWrapper?: ElementRef;
  @ViewChild('smsPhoneInput') smsPhoneInput?: ElementRef<HTMLInputElement>;
  userQna: QnA[] = [];
  userQuery: string = "";
  currentUserQuery: string = ""
  currentIntent: string = "";
  queryResponseReceived: boolean = true;
  isUserSpeaking: boolean = false;
  isQueryComeAsVoiceInput: boolean = false;
  isFullScreen: boolean = false;
  isLandingScreenShown = false;
  isBackendServerAvailable: boolean = true;
  currentAgent: string = "VirtualAgent";
  responseTypingSpeed = 50;
  isHumanAgentDisconnected: boolean = false;
  /** Agent closed the session; the conversation is terminal and input is blocked. */
  isConversationClosed: boolean = false;
  private unsubscribe$ = new Subject<void>();
  private rSocketMessageSubscription!: Subscription;
  private realtimeService!: IRealtimeService;
  humanAgentFeature: boolean = true; 
  pickerVisible = false;
  chatBotStyles!: ChatBotStyleAndConfig;
  lastUserQnaChild!: ElementRef;
  customerName: string = ""; 
  welcomeMessageDateTime: Date = new Date();
  customerWelcomeMessage: string = "";
  @Input() isWelcomeScreen!: boolean;
  agentName: string="";
  smsChannelOption: ChannelAvailability | null = null;
  smsSwitchPromptOpen = false;
  smsSwitchPhone = "";
  smsSwitchError: string | null = null;
  smsSwitchCompleted = false;
  smsSwitchConfirmation = "";
  smsSwitchSubmitting = false;
  smsSwitchResolving = false;
  private smsOptionsConversationId: string | null = null;
  private smsOptionsLoaded = false;
  private smsOptionsSub: Subscription | null = null;
  private presenceTimer: ReturnType<typeof setInterval> | null = null;
  private static readonly PRESENCE_HEARTBEAT_MS = 20000;

  private static readonly SMS_REASON_NOT_ACTIVATED = "SMS functionality not Activated";
  private static readonly SMS_REASON_NO_MOBILE = "Customer Mobile not recorded";
  private static readonly SMS_REASON_INVALID_MOBILE = "Not a valid Mobile phone number";

  private readonly humanAgentAttachmentDefaults = {
    supportedTypes: "jpg,jpeg,png,svg",
    maxFiles: 5,
    maxFileSizeBytes: 10 * 1024 * 1024,
    prompt: "Attach photos or share a message with your agent.",
  };

  /** Current conversationContextId (from session), consumed by rich-message widgets that upload files. */
  get conversationContextId(): string {
    try {
      const ctxStr = this._sessionService.getContextInfo();
      const ctx = ctxStr ? JSON.parse(ctxStr) : null;
      return ctx?.conversationContextId ?? "";
    } catch {
      return "";
    }
  }

  /** Wire/API component type for the attachments widget (booking flow). */
  static readonly ATTACHMENTS_COMPONENT_TYPE = "ATTACHMENTS";

  /**
   * Input-bar attachment tray state. When the backend pushes an unsubmitted
   * ATTACHMENTS rich message, the tray offers attach files and send-with-files.
   * A text-only Send dismisses the tray and sends the message as normal chat (booking notes).
   */
  pendingFiles: { tempId: string; file: File; previewUrl: string; status: "pending" | "uploading" | "uploaded" | "error"; attachmentId?: string; errorMsg?: string }[] = [];
  widgetInlineError: string | null = null;
  isSubmittingWidget: boolean = false;
  private readonly _trayPreviewUrls = new Map<string, string>();
  /** Object URLs for images shown in submitted user bubbles (revoked on destroy). */
  private readonly _submittedBubblePreviewUrls: string[] = [];

  /** Returns the latest active (unsubmitted) ATTACHMENTS widget entry, or null. */
  get activeNotesWidget(): { qnaId: string; messageId: string; payload: Record<string, any>; state: Record<string, any> } | null {
    for (let i = this.userQna.length - 1; i >= 0; i--) {
      const att = this.userQna[i]?.answer?.attachment;
      if (!att) continue;
      if (!this.isAttachmentsComponentType(att.type)) continue;
      if (att.state?.["submitted"]) continue;
      return {
        qnaId: this.userQna[i].id,
        messageId: att.messageId ?? "",
        payload: att.payload ?? {},
        state: att.state ?? {},
      };
    }
    return null;
  }

  get isConversationAttachmentConfigEnabled(): boolean {
    const raw =
      this.chatBotStyles?.conversationAttachmentEnabled ??
      this._configService.getCurrentConfig()?.conversationAttachmentEnabled;
    if (raw === false || raw === "false") {
      return false;
    }
    return raw === true || raw === "true";
  }

  /** Live human-agent chat: attachment tray without an AI rich widget row. */
  get isHumanAgentComposerMode(): boolean {
    return (
      this.humanAgentFeature &&
      this.isConversationAttachmentConfigEnabled &&
      this.currentAgent === "HumanAgent" &&
      !this.isHumanAgentDisconnected &&
      !this.activeNotesWidget
    );
  }

  get showComposerAttachmentTray(): boolean {
    if (this.isHumanAgentComposerMode) {
      return true;
    }
    if (!this.activeNotesWidget) {
      return false;
    }
    // Booking ATTACHMENTS widget: wait until the in-flight VA reply finishes streaming.
    return !this.isVirtualAgentResponseInProgress();
  }

  /** True while a VA bubble is still loading or undergoing smooth character streaming. */
  private isVirtualAgentResponseInProgress(): boolean {
    for (const entry of this.userQna) {
      if (this._chatStateService.isActivelyStreaming(entry.id)) {
        return true;
      }
      const ans = entry.answer;
      if (
        ans.receivedFrom === "VirtualAgent" &&
        (ans.id === "" || ans.id === "dummyId")
      ) {
        return true;
      }
    }
    return false;
  }

  get widgetSupportedExtensions(): string[] {
    const raw = this.isHumanAgentComposerMode
      ? this.humanAgentAttachmentDefaults.supportedTypes
      : this.activeNotesWidget?.payload?.["supportedTypes"];
    if (typeof raw !== "string" || !raw.trim()) return [];
    return raw.split(",").map((t) => t.trim().toLowerCase().replace(/^\./, "")).filter(Boolean);
  }

  get widgetAttachmentsEnabled(): boolean {
    if (this.isHumanAgentComposerMode) return true;
    if (!this.activeNotesWidget) return false;
    const v = this.activeNotesWidget.payload?.["attachmentsEnabled"];
    if (v === false || v === "false") return false;
    return true;
  }

  get widgetAcceptAttr(): string {
    return this.widgetSupportedExtensions.map((ext) => `.${ext}`).join(",");
  }

  get widgetMaxFiles(): number {
    const raw = this.isHumanAgentComposerMode
      ? this.humanAgentAttachmentDefaults.maxFiles
      : this.activeNotesWidget?.payload?.["maxFiles"];
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 5;
  }

  get widgetMaxFileSizeBytes(): number {
    const raw = this.isHumanAgentComposerMode
      ? this.humanAgentAttachmentDefaults.maxFileSizeBytes
      : this.activeNotesWidget?.payload?.["maxFileSizeBytes"];
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1 * 1024 * 1024;
  }

  get widgetMaxFileSizeLabel(): string {
    const mb = this.widgetMaxFileSizeBytes / (1024 * 1024);
    return mb.toFixed(2).replace(/\.?0+$/, "");
  }

  get widgetPromptText(): string {
    if (this.isHumanAgentComposerMode) {
      return this.humanAgentAttachmentDefaults.prompt;
    }
    const v = this.activeNotesWidget?.payload?.["prompt"];
    return typeof v === "string" && v.trim() ? v : "Attach photos or images for this booking.";
  }

  get widgetCanAttachMore(): boolean {
    if (!this.widgetAttachmentsEnabled) return false;
    return !this.isSubmittingWidget && this.pendingFiles.length < this.widgetMaxFiles;
  }

  constructor(
    private _chatStateService: ChatStateService,
    private _chatService: ChatService,
    private _sessionService: SessionService,
    private _responseStreamService: ResponseStreamService,
    private _voiceRecognitionService: VoiceRecognitionService,
    private _textToSpeechService: TextToSpeechService,
    private _configService: ConfigService,
    private _runtimeConfigService: RuntimeConfigService,
    private _cdr: ChangeDetectorRef,
    private _rSocketService: RSocketService,
    private _realtimeServiceFactory: RealtimeServiceFactory,
    private _componentInteractionService: ComponentInteractionService,
    private _attachmentService: ConversationAttachmentService,
    private renderer: Renderer2
  ) { }

  ngOnInit(): void {
    this.realtimeService = this._realtimeServiceFactory.createService();
    this.handleSubscriptions(); 
    this.updateClassVariables();
    this._runtimeConfigService.config$.subscribe((cfg) => {
      this.humanAgentFeature = cfg.humanAgentFeature;
      if (cfg.humanAgentFeature && !this.rSocketMessageSubscription) {
        this.getAndProcessHumanAgentMessages();
      }
    });
    if(this.humanAgentFeature) 
      this.getAndProcessHumanAgentMessages();

    try {
      const contextInfoStr = this._sessionService.getContextInfo();
      const contextInfo = contextInfoStr ? JSON.parse(contextInfoStr) : {};
      if (contextInfo?.conversationContextId && this.realtimeService && typeof this.realtimeService.joinConversation === 'function') {
        console.log('[ChatBubble] ngOnInit - joining existing conversation group:', contextInfo.conversationContextId);
        this.realtimeService.joinConversation(contextInfo.conversationContextId);
      }
      this.refreshSmsChannelOption();
    } catch {}
    this.syncPresenceHeartbeat();
  }

  ngAfterViewInit(): void {
    this.adjustTextAreaHeight();
    this.scrollToBottom();
    setTimeout(() => {
      if (this.textArea && this.textArea.nativeElement) {
        this.textArea.nativeElement.focus();
      }
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isWelcomeScreen'] && !this.isWelcomeScreen) {
      this.refreshSmsChannelOption();
      setTimeout(() => {
        this.refreshSmsChannelOption();
        this.syncPresenceHeartbeat();
        this.textArea?.nativeElement?.focus();
      });
    }
  }

  @HostListener('document:click', ['$event.target'])
  public onClick(targetElement: any) {
    const clickedInside = this.pickerWrapper?.nativeElement?.contains(targetElement) ?? false;
    if (!clickedInside) {
      this.pickerVisible = false;
    }
  }

  @HostListener("pagehide")
  onPageHide(): void {
    this.stopPresenceHeartbeat(true);
    this.realtimeService?.disconnect();
  }

  handleSubscriptions() {
    this._chatStateService.usersQna$.subscribe((qna: any) => {
      this.userQna = qna;
      this.syncHumanAgentSessionFromChat();
      this.refreshSmsChannelOption();
      this.syncPresenceHeartbeat();
    });
    this._configService.chatBotStyleAndConfig$.subscribe((config) => {
      this.chatBotStyles = config;
      this.refreshSmsChannelOption();
      this._cdr.detectChanges();
    });
    this._chatStateService.isQueryResponseReceived$.subscribe((value: any) => this.queryResponseReceived = value); 
    this._chatStateService.isChatWindowFullScreen$.subscribe((value: any) => this.isFullScreen = value); 
    this._voiceRecognitionService.speechInput().subscribe(text => {
      this.setUserQuery(text);
    });
    this._chatStateService.customerName$.subscribe((name: string) => {
      this.customerName = name; 
      this.welcomeMessageDateTime = new Date(); 
    });
    this._chatStateService.customerWelcomeMessage$.subscribe((value: string) => {this.customerWelcomeMessage = value});
    this._chatStateService.agentName$.subscribe((name: string) => {
      this.agentName = name; 
    });
  }

  updateClassVariables() {
    this.humanAgentFeature = this._runtimeConfigService.snapshot.humanAgentFeature;
  }

  private syncPresenceHeartbeat(): void {
    if (this.conversationContextId && !this.isConversationClosed && !this.isWelcomeScreen) {
      this.startPresenceHeartbeat();
      return;
    }
    this.stopPresenceHeartbeat(false);
  }

  private startPresenceHeartbeat(): void {
    if (this.presenceTimer) {
      return;
    }
    this.reportPresenceOnline();
    this.presenceTimer = setInterval(() => this.reportPresenceOnline(), ChatBubbleComponent.PRESENCE_HEARTBEAT_MS);
  }

  private stopPresenceHeartbeat(sendOffline: boolean): void {
    if (this.presenceTimer) {
      clearInterval(this.presenceTimer);
      this.presenceTimer = null;
    }
    if (sendOffline && this.conversationContextId) {
      this._chatService.reportPresenceKeepalive(this.conversationContextId, false);
    }
  }

  private reportPresenceOnline(): void {
    const conversationContextId = this.conversationContextId;
    if (!conversationContextId) {
      return;
    }
    this._chatService.reportPresence(conversationContextId, true).pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe({ error: () => {} });
  }

  getAndProcessHumanAgentMessages() {
    // Guard against double-subscribe (ngOnInit can reach this twice), which duplicates messages.
    if (this.rSocketMessageSubscription) {
      return;
    }
    this.rSocketMessageSubscription = this.realtimeService.messages$.subscribe((msg) => {
      if (msg) {
        console.log("[STOMP] Received message:", msg);
        // Per-conversation STOMP subscription: topic is already scoped to this
        // conversation so no client-side conversationContextId filter is required.
        // We only skip USER echo messages (sent by us, not from the agent/AI).
        if (!this.isHumanAgentDisconnected && this.agentName === "" && msg.messageSource === "HUMAN_AGENT_RESPONSE") {
          this._chatStateService.setAgentName(msg.agentName);
        }

        if (msg.messageSource !== "USER") {
          if (msg.messageState === "ACK_STARTED") {
            this._chatStateService.setQueryResponseReceived(false);
            this._cdr.detectChanges();
          }
          else if (msg.messageState === "AGENT_CLAIMED") {
            // Capture the agent's name from the claim event so bubbles show it instead of "Human agent".
            if (msg.agentName) {
              this._chatStateService.setAgentName(msg.agentName);
            }
            this.activateHumanAgentSession(true);
            this._chatStateService.setQueryResponseReceived(true);
            this._chatStateService.removeTrailingStandaloneAgentLoader("VirtualAgent");
            this._cdr.detectChanges();
          }
          else if (msg.messageState === "SESSION_CLOSED") {
            // Agent closed the session.
            this.handleSessionClosed();
          }
          else if (msg.attachment) {
            // AI message that carries an interactive widget (rating, quick replies, etc.).
            // Render via the rich-message dispatcher; skip the streaming code path.
            this.handleRichAiMessage(msg.message, msg.attachment);
          }
          else if (msg.messageSource === "AI" || (msg.messageSource === "SYSTEM_RESPONSE" && msg.message)) {
            this.updateVirtualAgentRealtimeResponse(msg.message);
          }
          else if (this.currentAgent == 'VirtualAgent') {
            this.activateHumanAgentSession(true);
          }
          if (msg.messageSource !== "AI" && msg.messageSource !== "SYSTEM_RESPONSE" && !msg.messageState && msg.message === this._runtimeConfigService.snapshot.humanAgentSessionClosedMessage) {
            this.handleSessionClosed();
          }
          else if (msg.messageSource !== "AI" && msg.messageSource !== "SYSTEM_RESPONSE" && !msg.messageState && msg.message) {
            console.log("Adding human agent message to chat:", msg.message);
            this.updateHumanAgentResponse(msg.message);
          }
        } else {
          // USER echo — we sent this message; no UI action needed
          console.log("[STOMP] Skipping USER-source echo message");
        }
      }
    });
  }

  /**
   * Switches the widget to human-agent mode and enables the attachment composer.
   * @param showJoinedBanner When true and we were on the virtual agent, renders the divider banner once.
   */
  private activateHumanAgentSession(showJoinedBanner: boolean): void {
    const wasVirtual = this.currentAgent === "VirtualAgent";
    this.currentAgent = "HumanAgent";
    this._chatStateService.removeTrailingStandaloneAgentLoader("VirtualAgent");

    if (showJoinedBanner && wasVirtual && this.userQnaElements?.last?.nativeElement) {
      const div = this.renderer.createElement("div");
      const classes = ["w-full", "pt-4", "flex", "items-center"];
      classes.forEach((cls) => this.renderer.addClass(div, cls));
      div.innerHTML = `<div class="flex-grow h-px bg-gray-300"></div>
                <div class="w-[15rem] px-1 py-1 rounded-full border text-center text-[var(--textNotificationColor)] font-semibold font-['Open_Sans'] opacity-80 text-xs">
                  Human agent has joined the conversation
                </div>
              <div class="flex-grow h-px bg-gray-300"></div>`;
      this.renderer.appendChild(this.userQnaElements.last.nativeElement, div);
      this.scrollToBottom();
    }
    this._cdr.detectChanges();
  }

  private deactivateHumanAgentSession(): void {
    const clearTray =
      this.isConversationAttachmentConfigEnabled && this.pendingFiles.length > 0;
    this.currentAgent = "VirtualAgent";
    if (clearTray) {
      this.clearPendingTiles();
    }
  }

  /** Re-enable HA composer after refresh when the thread already has human-agent replies. */
  private syncHumanAgentSessionFromChat(): void {
    if (!this.humanAgentFeature || this.isHumanAgentDisconnected) {
      return;
    }
    const hasHumanAgentReply = this.userQna.some(
      (q) =>
        q.answer?.receivedFrom === "HumanAgent" &&
        !!q.answer?.message &&
        q.answer.id !== "" &&
        q.answer.id !== "dummyId"
    );
    if (hasHumanAgentReply && this.currentAgent !== "HumanAgent") {
      this.activateHumanAgentSession(false);
    }
  }

  updateHumanAgentResponse(message: string) {
      this._chatStateService.removeTrailingStandaloneAgentLoader("VirtualAgent");
      const currentQnA = this._chatStateService.getQna();
      currentQnA.answer.message = message;
      currentQnA.answer.receivedFrom = "HumanAgent";
      this._chatStateService.addEntryToUsersQna(currentQnA);
      const qnaId = this.userQna[this.userQna.length - 1].id;
      this._chatStateService.appendHumanAgentResponseAndAnswerId(qnaId, message, Math.random() + "");
      this._chatStateService.setQueryResponseReceived(true);
      this.scrollToBottom(); 
      this._cdr.detectChanges(); 
  }

  updateVirtualAgentRealtimeResponse(message: string) {
    this._chatStateService.removeTrailingStandaloneAgentLoader("VirtualAgent");
    this._chatStateService.addStandaloneStreamingAgentMessage(message, "VirtualAgent");
    this._cdr.detectChanges();
    const standaloneQna = this.userQna[this.userQna.length - 1];

    this.scrollToBottom();
    this.applySmoothStreamingEffect(standaloneQna.id);
    this._chatStateService.gracefullyFinishStreaming(standaloneQna.id, () => {
      this._chatStateService.updateQueryResponseId(standaloneQna.id, "ANSWER_id");
      this._chatStateService.setQueryResponseReceived(true);
      this._cdr.detectChanges();
      this.scrollToBottom();
    });
  }

  saveHumanAgentMessageThread(message: any) {
    const contextInfo =  JSON.parse(this._sessionService.getContextInfo() || ""); 
    const payload = {
      conversationContextId: contextInfo?.conversationContextId,
      incomingMessage: message,
      outgoingMessage: ""
    }
    this._chatService.saveHumanAgentMessageThread(payload).subscribe({
      next: (response: string) => {
        console.log(response);
      },
      error: (error: any) => {
        console.log("error while saving human agent messages");
      },
    });
  }

  /** Agent closed the session: banner + terminal input block + drop the dead conversation. */
  private handleSessionClosed(): void {
    if (this.isConversationClosed) {
      return; // already handled
    }
    if (this.currentAgent === "HumanAgent" || this.userQna.some((qna) => qna.answer.receivedFrom == "HumanAgent")) {
      this.showHumanAgentDisconnectionAlert();
    }
    this.deactivateHumanAgentSession();
    // Terminal state: block further input and leave the conversation's STOMP topic.
    this.isConversationClosed = true;
    this.stopPresenceHeartbeat(true);
    try {
      const closedCtxStr = this._sessionService.getContextInfo();
      const closedCtxId = closedCtxStr ? JSON.parse(closedCtxStr)?.conversationContextId : null;
      if (closedCtxId && typeof this.realtimeService?.leaveConversation === 'function') {
        this.realtimeService.leaveConversation(closedCtxId);
      }
    } catch { /* ignore malformed context */ }
    this._sessionService.removeContextInfo();
    this._cdr.detectChanges();
  }

  showHumanAgentDisconnectionAlert(){
    this.isHumanAgentDisconnected = true;
    if (this.isConversationAttachmentConfigEnabled) {
      this.clearPendingTiles();
    }
    this._chatStateService.setQueryResponseReceived(true); 
    const div = this.renderer.createElement('div');
    const classes = ['w-full', 'pt-4', 'flex', 'items-center'];
    classes.forEach(cls => this.renderer.addClass(div, cls));
    div.innerHTML = `<div class="flex-grow h-px bg-gray-300"></div>
        <div class="w-[15rem] px-1 py-1 rounded-full border text-center text-[var(--textNotificationColor)] font-semibold font-['Open_Sans'] opacity-80 text-xs">
          Human agent has left the conversation
        </div>
      <div class="flex-grow h-px bg-gray-300"></div>`;
    this.renderer.appendChild(this.userQnaElements.last.nativeElement, div);
    this.scrollToBottom();
    this._cdr.detectChanges();
    // Keep agentName intact: the label binding is reactive; clearing it reverts closed bubbles to "Human agent".
    setTimeout(() => {
      this.isHumanAgentDisconnected = false;
    }, environment.humanAgentDisconnectionTime);
  }

  async handleStartChat() {
    this._sessionService.removeContextInfo();
    const payload = {
      intent: "",
      ownerId: this.chatBotStyles.tenantCode,
      customerName: this.customerName || "",
    };
    try {
      const response = await firstValueFrom(this._chatService.startChatWithIntent(payload));
      await this._sessionService.setContextInfo(JSON.stringify(response));
      
      console.log('[ChatBubble] Starting chat - conversation ID:', response.conversationContextId);
      console.log('[ChatBubble] Realtime service available:', !!this.realtimeService);
      console.log('[ChatBubble] joinConversation method available:', typeof this.realtimeService?.joinConversation === 'function');
      
      if (this.realtimeService && typeof this.realtimeService.joinConversation === 'function') {
        console.log('[ChatBubble] Calling joinConversation with ID:', response.conversationContextId);
        this.realtimeService.joinConversation(response.conversationContextId);
      } else {
        console.warn('[ChatBubble] Cannot join conversation - realtime service or method not available');
      }
      this.refreshSmsChannelOption(true);
      this.syncPresenceHeartbeat();
    } catch (error) {
      console.log("Error starting chat:", error);
    }
  }

  async getAnswer(isQueryInputInVoice: boolean) {
    this.resetTextAreaHeight();
    this._chatStateService.setQueryResponseReceived(false); 
    this.isQueryComeAsVoiceInput = isQueryInputInVoice;

    if (this.smsSwitchCompleted) {
      this.switchBackToWebChat();
    }

    if(this.userQna.length == 0) {
      await this.handleStartChat();
    } else {
      const contextInfoStr = this._sessionService.getContextInfo();
      const contextInfo = contextInfoStr ? JSON.parse(contextInfoStr) : {};

      if (contextInfo?.conversationContextId && this.realtimeService && typeof this.realtimeService.joinConversation === 'function') {
        console.log('[ChatBubble] Existing conversation - joining SignalR group:', contextInfo.conversationContextId);
        this.realtimeService.joinConversation(contextInfo.conversationContextId);
      }
    }
    
    const currentQnA = this._chatStateService.getQna();
    currentQnA.question.message = this.userQuery;
    this.currentUserQuery = this.userQuery;
    this.setUserQuery("");
    this._chatStateService.addEntryToUsersQna(currentQnA);
    this._cdr.detectChanges();

    if(this.currentAgent === "VirtualAgent") {
      console.log("calling stream api");
      
      currentQnA.answer.receivedFrom = this.currentAgent;
      this.getResponseStreamAndUpdateQnA(currentQnA.id, isQueryInputInVoice);
    }
    else {
      console.log("waiting for human agent connection response");
      currentQnA.answer.receivedFrom = this.currentAgent;
      this.sendQueryToHumanAgent(this.currentUserQuery);
      this.saveHumanAgentMessageThread(this.currentUserQuery); 
    }
    setTimeout(() => {
      this.scrollToBottom();
      this._cdr.detectChanges();
    }, 50)
  }

  getResponseStreamAndUpdateQnA(currentQnAId: string, isInputInVoice: boolean) {
    let isFirstChunkRecevied = false;
    const tenantCode = this.chatBotStyles.tenantCode ?? "";
    this._responseStreamService.connect(`${environment.apiURL}chat`, this.currentUserQuery, tenantCode).subscribe(
      (response: any) => {
        console.log(response);

        if (!isFirstChunkRecevied) {
          response.content = response.content.replace(/^\s+/, '');
        }

        const isFirstChunk = !isFirstChunkRecevied;
        isFirstChunkRecevied = true;

        if (response.stop !== true) {
          this._chatStateService.updateQueryResponseStream(currentQnAId, response.content.replaceAll('"', ''));
          if (isFirstChunk) {
            this._cdr.detectChanges();
          }
          this.applySmoothStreamingEffect(currentQnAId);
        } else if (response.stop == true) {
          this._chatStateService.gracefullyFinishStreaming(currentQnAId, () => {
            const qna = this.userQna.find(q => q.id === currentQnAId);
            const hasAnswer = !!qna?.answer.message && qna.answer.message.trim().length > 0;
            const hasQuestion = !!qna?.question.message;
            if (hasAnswer) {
              this._chatStateService.updateQueryResponseId(currentQnAId, "ANSWER_id");
            } else if (hasQuestion) {
              // LLM produced no visible answer (e.g. it intentionally stayed silent after
              // calling request_user_feedback). Drop the empty answer rather than rendering
              // a blank bubble; the user's question bubble is preserved.
              this._chatStateService.clearAnswerForQna(currentQnAId);
            } else {
              this._chatStateService.removeQnaEntry(currentQnAId);
            }
            this._chatStateService.setQueryResponseReceived(true);
            this._cdr.detectChanges();
            this.scrollToBottom();
          });
        }
      },
      (error) => {
        this._chatStateService.setQueryResponseReceived(true); 
        console.error("Error:", error); 
      }
    );
  }

  sendQueryToHumanAgent(currentUserQuery: string) {
    const contextInfo = JSON.parse(this._sessionService.getContextInfo() || "");
    console.log('[ChatBubble] Sending query to human agent - conversation ID:', contextInfo?.conversationContextId);
    if (this.realtimeService && typeof this.realtimeService.joinConversation === 'function') {
      console.log('[ChatBubble] Calling joinConversation for human agent query');
      this.realtimeService.joinConversation(contextInfo?.conversationContextId);
    } else {
      console.warn('[ChatBubble] Cannot join conversation for human agent query');
    }
    
    this.realtimeService.sendMessage({
      conversationContextId: contextInfo?.conversationContextId,
      message: currentUserQuery,
      messageSource: "USER",
      agentName: this.agentName || ""
    });
  }

  /**
   * How many `#messageContainer` elements this QnA row renders (must stay aligned with
   * chat-bubble.component.html — rich rows and VA loader-without-chunks contribute 0).
   */
  private messageContainersContributedByEntry(entry: QnA): number {
    const ans = entry.answer;
    const vaStreamingLoader =
      ans.receivedFrom === "VirtualAgent" &&
      (ans.id === "" || ans.id === "dummyId");
    if (vaStreamingLoader && ans.id === "dummyId") {
      return 1;
    }
    const plainBubble =
      (ans.id === "ANSWER_id" ||
        (ans.id !== "" && ans.receivedFrom !== "VirtualAgent")) &&
      !ans.attachment;
    return plainBubble ? 1 : 0;
  }

  private messageContainerSlotForQna(qnaId: string): number {
    const entryIndex = this.userQna.findIndex((q) => q.id === qnaId);
    if (entryIndex < 0) return -1;
    let slot = 0;
    for (let i = 0; i < entryIndex; i++) {
      slot += this.messageContainersContributedByEntry(this.userQna[i]);
    }
    return slot;
  }

  applySmoothStreamingEffect(qnaId: string) {
    const entryIndex = this.userQna.findIndex((q) => q.id === qnaId);
    if (entryIndex < 0) return;

    const slot = this.messageContainerSlotForQna(qnaId);
    const containers = this.messageContainers.toArray();
    if (slot < 0 || slot >= containers.length) return;

    const row = this.userQna[entryIndex];
    this._chatStateService.smoothStreamResponse(
      row.answer.message,
      containers[slot]!.nativeElement,
      qnaId,
      this.modalBody?.nativeElement
    );
  }

  applyTypingEffect(index: number, message: string) {
    const containers = this.messageContainers.toArray();
    if (index >= 0 && index < containers.length) {
      const container = containers[index];
      const element = container.nativeElement;
      this._chatStateService.typeWriter(
        message,
        element,
        this.responseTypingSpeed
      );
    }
  }

  formatMessage(message: string, qnaId: string, qnaIndex: number): string {
    return this._chatStateService.formatResponseMessage(message);
  }

  virtualAgentSpeak(message: string, qnaId: string, qnaIndex: number) {
    this._chatStateService.updateVirtualAgentSpeaking(qnaId, true);
    const currentQna = this._chatStateService.getUserQnaIndex(qnaIndex);

    if (currentQna.answer.isVirtualAgentSpeakingPaused) {
      this._textToSpeechService.resume();
      this._chatStateService.updateVirtualAgentSpeakingPaused(qnaId, false);
      this._cdr.detectChanges();
    }
    else {
      this._textToSpeechService.speak(message, qnaId, this._cdr);
    }
    this._cdr.detectChanges();
  }

  pauseVirtualAgentSpeaking(qnaId: string, qnaIndex: number) {
    this._chatStateService.updateVirtualAgentSpeaking(qnaId, false);
    this._chatStateService.updateVirtualAgentSpeakingPaused(qnaId, true);
    this._cdr.detectChanges();
    const currentQna = this._chatStateService.getUserQnaIndex(qnaIndex);
    this._textToSpeechService.pause();
    this._cdr.detectChanges();
  }

  scrollToBottom(): void {
    const element = this.modalBody.nativeElement;
    setTimeout(() => {
      element.scrollTop = element.scrollHeight;
    }, 50);
  }

  closeChatModal() {
    this.closeModalEvent.emit(false);
  }

  proceedQuery() {
    // Conversation is closed — reject further input.
    if (this.isConversationClosed) {
      return;
    }
    if (this.activeNotesWidget) {
      if (this.pendingFiles.length > 0) {
        this.submitActiveWidget();
        return;
      }
      if (!this.isUserQueryEmpty()) {
        // User is replying in chat (e.g. booking note) — close the attachment tray without a "Skipped" line.
        this.dismissActiveAttachmentWidget({ thenSendChat: true });
        return;
      }
      return null;
    }
    if (this.isHumanAgentComposerMode) {
      if (this.pendingFiles.length > 0 || !this.isUserQueryEmpty()) {
        void this.submitHumanAgentComposerMessage();
      }
      return null;
    }
    return ((!this.isUserQueryEmpty() && this.queryResponseReceived) || this.currentAgent == 'HumanAgent') ? this.getAnswer(false) : null; 
  }

  handleKeyDownEnter(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.proceedQuery();    
    }
  }

  onQueryInput(event: Event) {
    this.userQuery = (event.target as HTMLTextAreaElement).value;
    this.adjustTextAreaHeight();
  }

  private setUserQuery(value: string) {
    this.userQuery = value;
    if (this.textArea?.nativeElement) {
      this.textArea.nativeElement.value = value;
      this.adjustTextAreaHeight();
    }
  }

  isUserQueryEmpty() {
    return this.userQuery.trim().length > 0 ? false : true; 
  }

  /**
   * Backend writes a USER transcript like "Rated this conversation 3 out of 5." after feedback
   * submit. Hide it in the bubble list only (history / analytics unchanged server-side).
   */
  isFeedbackRatingTranscript(htmlOrPlain: string | undefined | null): boolean {
    if (!htmlOrPlain?.trim()) return false;
    const plain = htmlOrPlain.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    return /(\byou\s+)?rated\s+this\s+conversation\s+\d+\s+out\s+of\s+\d+/i.test(
      plain
    );
  }

  trackByEntryId(_index: number, entry: QnA): string {
    return entry.id;
  }

  togglePicker() {
    this.pickerVisible = !this.pickerVisible;
  }

  addEmoji(event: any) {
    if (!this.textArea?.nativeElement) return;
    const textarea = this.textArea.nativeElement;
    const start = textarea.selectionStart ?? textarea.value.length;
    const end = textarea.selectionEnd ?? textarea.value.length;
    textarea.setRangeText(event.emoji.native, start, end, 'end');
    textarea.dispatchEvent(new InputEvent('input', { bubbles: true }));
    textarea.focus();
  }

  adjustTextAreaHeight() {
    const el = this.textArea?.nativeElement;
    if (el) {
      el.style.height = 'auto';
      const scrollHeight = el.scrollHeight;
      const maxHeight = 96;
      el.style.height = Math.min(scrollHeight, maxHeight) + 'px';
      el.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
    }
  }

  ngOnDestroy(): void {
    this.stopPresenceHeartbeat(true);
    this._chatStateService.clearStreamingIntervals();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.smsOptionsSub?.unsubscribe();
    if (this.rSocketMessageSubscription) {
      this.rSocketMessageSubscription.unsubscribe();
    }
    if (this.realtimeService) {
      this.realtimeService.disconnect();
    }
    this._trayPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    this._trayPreviewUrls.clear();
    this._submittedBubblePreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    this._submittedBubblePreviewUrls.length = 0;
  }

  resetTextAreaHeight() {
    if (!this.textArea?.nativeElement) return;
    this.textArea.nativeElement.style.height = 'auto';
  }

  isActivelyStreaming(qnaId: string): boolean {
    return this._chatStateService.isActivelyStreaming(qnaId);
  }

  isStreamingTable(qnaId: string): boolean {
    return this._chatStateService.isStreamingTable(qnaId);
  }

  /**
   * Realtime hook: an AI message arrived with an interactive UI component attached.
   * The chat-state service de-dupes on attachment.messageId, so re-deliveries are safe.
   */
  handleRichAiMessage(message: string | undefined, attachment: any) {
    if (!attachment || !attachment.type || !attachment.messageId) {
      console.warn("Discarding rich AI message with malformed attachment", attachment);
      return;
    }
    this._chatStateService.suppressVirtualAgentLoaderWhenRichMessageArrives();
    this._chatStateService.appendRichAiMessage(message ?? "", attachment);
    const deferReady =
      this.isAttachmentsComponentType(attachment.type) &&
      this.isVirtualAgentResponseInProgress();
    if (!deferReady) {
      this._chatStateService.setQueryResponseReceived(true);
    }
    this._cdr.detectChanges();
    this.scrollToBottom();
  }

  isAttachmentsComponentType(type: string | undefined | null): boolean {
    return type === ChatBubbleComponent.ATTACHMENTS_COMPONENT_TYPE;
  }

  // --- Input-bar ATTACHMENTS tray handlers -----------------------------------

  onTrayFilePicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const picked = input.files ? Array.from(input.files) : [];
    input.value = "";
    if (!picked.length || !this.showComposerAttachmentTray) return;

    this.widgetInlineError = null;
    const allowed = this.widgetSupportedExtensions;
    const maxSize = this.widgetMaxFileSizeBytes;

    const accepted: File[] = [];
    for (const file of picked) {
      const ext = this.extractExt(file.name);
      if (allowed.length && (!ext || !allowed.includes(ext))) {
        this.widgetInlineError = `Only ${allowed.map((e) => e.toUpperCase()).join(", ")} files are supported.`;
        continue;
      }
      if (file.size > maxSize) {
        this.widgetInlineError = `File too large (max ${this.widgetMaxFileSizeLabel} MB).`;
        continue;
      }
      accepted.push(file);
    }
    if (!accepted.length) return;

    const slots = this.widgetMaxFiles - this.pendingFiles.length;
    if (accepted.length > slots) {
      this.widgetInlineError = `You can attach up to ${this.widgetMaxFiles} files.`;
      accepted.splice(slots);
    }
    for (const f of accepted) this.addPendingTile(f);
  }

  private addPendingTile(file: File): void {
    const tempId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const previewUrl = URL.createObjectURL(file);
    this._trayPreviewUrls.set(tempId, previewUrl);
    this.pendingFiles = [
      ...this.pendingFiles,
      { tempId, file, previewUrl, status: "pending" },
    ];
  }

  onTrayRemove(tile: { tempId: string }): void {
    if (this.isSubmittingWidget) return;
    const url = this._trayPreviewUrls.get(tile.tempId);
    if (url) {
      URL.revokeObjectURL(url);
      this._trayPreviewUrls.delete(tile.tempId);
    }
    this.pendingFiles = this.pendingFiles.filter((f) => f.tempId !== tile.tempId);
    if (this.widgetInlineError) this.widgetInlineError = null;
  }

  /**
   * Marks the attachment widget submitted (no files), or continues with a normal chat send
   * when the user typed a message instead.
   */
  private dismissActiveAttachmentWidget(options: { thenSendChat?: boolean }): void {
    const active = this.activeNotesWidget;
    if (!active) {
      if (options.thenSendChat) {
        this.getAnswer(false);
      }
      return;
    }
    if (!this.conversationContextId) {
      this.widgetInlineError = "Missing conversation context.";
      return;
    }

    this.isSubmittingWidget = true;
    this.widgetInlineError = null;

    this._componentInteractionService
      .submit(this.conversationContextId, active.messageId, ChatBubbleComponent.ATTACHMENTS_COMPONENT_TYPE, {
        notes: "",
        attachmentIds: [],
      })
      .subscribe({
        next: (updated) => {
          this._chatStateService.updateAttachmentState(active.qnaId, updated.state ?? {});
          this.clearPendingTiles();
          this.isSubmittingWidget = false;
          this._cdr.detectChanges();
          if (options.thenSendChat) {
            this.getAnswer(false);
          }
        },
        error: (error) => {
          this.isSubmittingWidget = false;
          this.widgetInlineError = "Could not continue. Please try again.";
          console.error("Failed to dismiss attachment widget", error);
          this._cdr.detectChanges();
        },
      });
  }

  /**
   * Human-agent chat: upload files (if any), then send text + attachment summary via STOMP/HA log.
   * Does not call component-submit.
   */
  async submitHumanAgentComposerMessage(): Promise<void> {
    if (!this.isHumanAgentComposerMode || this.isSubmittingWidget) return;
    this.widgetInlineError = null;

    if (!this.conversationContextId) {
      this.widgetInlineError = "Missing conversation context.";
      return;
    }

    const pendingNote = this.userQuery.trim();
    const toUpload = this.pendingFiles.filter((f) => f.status === "pending" || f.status === "error");
    if (!pendingNote && toUpload.length === 0 && !this.pendingFiles.some((f) => f.status === "uploaded")) {
      return;
    }

    this.isSubmittingWidget = true;
    try {
      const uploadedDisplays: QuestionUploadedFile[] = [];

      if (toUpload.length > 0) {
        for (const tile of toUpload) {
          tile.status = "uploading";
          tile.errorMsg = undefined;
        }
        this._cdr.detectChanges();

        try {
          const resp = await firstValueFrom(
            this._attachmentService.uploadAttachments(
              this.conversationContextId,
              toUpload.map((t) => t.file)
            )
          );
          const attachmentId = resp?.id != null ? String(resp.id) : "";
          if (!attachmentId || !resp.assets?.length) {
            throw new Error("Upload did not save files.");
          }
          for (const tile of toUpload) {
            tile.attachmentId = attachmentId;
            tile.status = "uploaded";
          }
          for (let i = 0; i < toUpload.length; i++) {
            const tile = toUpload[i];
            const asset = resp.assets[i];
            if (asset?.id != null) {
              uploadedDisplays.push(
                ...(await this.toUploadedFileDisplaysForAsset(tile, asset))
              );
            }
          }
        } catch (uploadErr) {
          for (const tile of toUpload) {
            tile.status = "error";
            tile.errorMsg = "Upload did not save the file. Please try again.";
          }
          this.widgetInlineError =
            uploadErr instanceof HttpErrorResponse
              ? this.extractUploadErrorMessage(uploadErr)
              : "Upload did not save the file. Please try again.";
          this._cdr.detectChanges();
          return;
        }
        this._cdr.detectChanges();
      }

      const attachedCount = this.pendingFiles.filter((f) => f.status === "uploaded").length;
      const transcript = this.buildWidgetSubmitTranscript(attachedCount, pendingNote);
      const bubbleText = this.buildWidgetBubbleText(attachedCount, pendingNote, uploadedDisplays.length > 0);
      const display = (bubbleText ?? "").trim();
      const apiMessage = (transcript ?? "").trim();
      if (!apiMessage && !display && uploadedDisplays.length === 0) {
        return;
      }

      this._chatStateService.setQueryResponseReceived(true);
      const currentQnA = this._chatStateService.getQna();
      currentQnA.question.message = display;
      if (uploadedDisplays.length) {
        currentQnA.question.uploadedFiles = uploadedDisplays;
      }
      currentQnA.answer.receivedFrom = "HumanAgent";
      this.currentUserQuery = apiMessage || display;
      this.setUserQuery("");
      this._chatStateService.addEntryToUsersQna(currentQnA);
      this._cdr.detectChanges();

      this.sendQueryToHumanAgent(this.currentUserQuery);
      this.saveHumanAgentMessageThread(this.currentUserQuery);
      this.clearPendingTiles();
      this.scrollToBottom();
    } catch (error) {
      this.widgetInlineError =
        error instanceof HttpErrorResponse
          ? this.extractUploadErrorMessage(error)
          : "Could not send your message. Please try again.";
      console.error("Failed to send human-agent message with attachments", error);
      this._cdr.detectChanges();
    } finally {
      this.isSubmittingWidget = false;
      this._cdr.detectChanges();
    }
  }

  async submitActiveWidget(): Promise<void> {
    const active = this.activeNotesWidget;
    if (!active || this.isSubmittingWidget) return;
    this.widgetInlineError = null;

    if (!this.conversationContextId) {
      this.widgetInlineError = "Missing conversation context.";
      return;
    }

    const pendingNote = this.userQuery.trim();
    const toUpload = this.pendingFiles.filter((f) => f.status === "pending" || f.status === "error");
    this.isSubmittingWidget = true;
    try {
      const uploadedDisplays: QuestionUploadedFile[] = [];

      for (const tile of toUpload) {
        tile.status = "uploading";
        tile.errorMsg = undefined;
        this._cdr.detectChanges();
        const resp = await this.uploadOnce(tile.file);
        const attachmentId = resp?.id != null ? String(resp.id) : "";
        if (!attachmentId || !resp.assets?.length) {
          tile.status = "error";
          tile.errorMsg = "Upload did not save the file. Please try again.";
          this.widgetInlineError = tile.errorMsg;
          this._cdr.detectChanges();
          return;
        }
        tile.attachmentId = attachmentId;
        tile.status = "uploaded";
        uploadedDisplays.push(...(await this.toUploadedFileDisplays(tile, resp)));
        this._cdr.detectChanges();
      }

      const attachmentIds = this.pendingFiles
        .filter((f) => f.status === "uploaded" && !!f.attachmentId)
        .map((f) => f.attachmentId!) as string[];
      const attachedCount = attachmentIds.length;

      const updated = await firstValueFrom(
        this._componentInteractionService.submit(
          this.conversationContextId,
          active.messageId,
          ChatBubbleComponent.ATTACHMENTS_COMPONENT_TYPE,
          { notes: "", attachmentIds }
        )
      );

      this._chatStateService.updateAttachmentState(active.qnaId, updated.state ?? {});

      const llmTranscript = this.buildWidgetSubmitTranscript(attachedCount, pendingNote);
      const bubbleText = this.buildWidgetBubbleText(attachedCount, pendingNote, uploadedDisplays.length > 0);
      this.sendWidgetSubmissionAsChat(llmTranscript, bubbleText, uploadedDisplays);

      this.clearPendingTiles();
      this.scrollToBottom();
    } catch (error) {
      if (this.pendingFiles.some((f) => f.status === "error")) {
        // uploadOnce already set tile-level error
      } else {
        this.widgetInlineError =
          error instanceof HttpErrorResponse
            ? this.extractUploadErrorMessage(error)
            : "Could not submit attachments. Please try again.";
        console.error("Failed to submit notes widget", error);
      }
      this._cdr.detectChanges();
    } finally {
      this.isSubmittingWidget = false;
      this._cdr.detectChanges();
    }
  }

  private uploadOnce(file: File): Promise<ConversationAttachmentResponse> {
    return firstValueFrom(
      this._attachmentService.uploadAttachment(this.conversationContextId, file)
    );
  }

  private async toUploadedFileDisplays(
    tile: { file: File; previewUrl: string },
    resp: ConversationAttachmentResponse
  ): Promise<QuestionUploadedFile[]> {
    const asset = resp.assets?.[0];
    if (asset?.id == null) {
      return [];
    }
    return this.toUploadedFileDisplaysForAsset(tile, asset);
  }

  private async toUploadedFileDisplaysForAsset(
    tile: { file: File; previewUrl: string },
    asset: { id: number | string; name?: string }
  ): Promise<QuestionUploadedFile[]> {
    const assetId = asset?.id;
    if (assetId == null) {
      return [];
    }
    // OD5: path is conversation-scoped; interceptor now injects X-Tenant-Code automatically.
    const assetUrl = `conversations/${this.conversationContextId}/attachments/${assetId}`;
    const displayName = tile.file.name || asset.name || "file";
    try {
      const objectUrl = await firstValueFrom(
        this._attachmentService.buildAssetObjectUrl(this.conversationContextId, assetId)
      );
      this._submittedBubblePreviewUrls.push(objectUrl);
      return [{ name: displayName, assetUrl, previewUrl: objectUrl }];
    } catch (err) {
      console.warn("Could not load uploaded asset for preview, using asset URL", err);
      return [{ name: displayName, assetUrl }];
    }
  }

  private extractUploadErrorMessage(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      return (err.error && (err.error.message || err.error.error)) || err.message || "Upload failed";
    }
    return "Upload failed";
  }

  private extractExt(name: string): string {
    const idx = name.lastIndexOf(".");
    return idx >= 0 ? name.substring(idx + 1).toLowerCase() : "";
  }

  getTrayExtLabel(file: File): string {
    return this.extractExt(file.name).toUpperCase() || "FILE";
  }

  private clearPendingTiles(): void {
    this._trayPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    this._trayPreviewUrls.clear();
    this.pendingFiles = [];
    this.widgetInlineError = null;
  }

  /** User-facing transcript after the attachment widget is submitted (files only; notes use normal chat). */
  private buildAttachmentsBubbleText(attachedCount: number): string {
    if (attachedCount > 0) {
      return `Attachment : ${attachedCount} ${attachedCount === 1 ? "file" : "files"} attached`;
    }
    return "Skipped adding attachments.";
  }

  /** Text shown in the user bubble (images render separately when uploads exist). */
  private buildWidgetBubbleText(attachedCount: number, note: string, hasImagePreviews: boolean): string {
    const trimmedNote = (note ?? "").trim();
    if (hasImagePreviews) {
      return trimmedNote;
    }
    return this.buildWidgetSubmitTranscript(attachedCount, note);
  }

  /** Single USER turn sent to the chat API (includes attachment summary for the LLM). */
  private buildWidgetSubmitTranscript(attachedCount: number, note: string): string {
    const parts: string[] = [];
    if (attachedCount > 0) {
      parts.push(this.buildAttachmentsBubbleText(attachedCount));
    }
    const trimmedNote = (note ?? "").trim();
    if (trimmedNote) {
      parts.push(trimmedNote);
    }
    if (parts.length === 0) {
      return this.buildAttachmentsBubbleText(0);
    }
    return parts.join("\n");
  }

  /**
   * Sends the post-widget-submit text through the same path a typed user message takes:
   * pushes a USER bubble locally and fires the chat stream so the backend records the
   * USER turn and the LLM produces its next response (e.g. continuing the booking flow).
   */
  private sendWidgetSubmissionAsChat(
    apiTranscript: string,
    bubbleText?: string,
    uploadedFiles?: QuestionUploadedFile[]
  ): void {
    const transcript = (apiTranscript ?? "").trim();
    const display = (bubbleText ?? transcript).trim();
    const hasUploads = (uploadedFiles?.length ?? 0) > 0;
    if (!transcript && !display && !hasUploads) return;

    this._chatStateService.setQueryResponseReceived(false);
    this.isQueryComeAsVoiceInput = false;

    const currentQnA = this._chatStateService.getQna();
    currentQnA.question.message = display;
    if (uploadedFiles?.length) {
      currentQnA.question.uploadedFiles = uploadedFiles;
    }
    currentQnA.answer.receivedFrom = this.currentAgent;

    this.currentUserQuery = transcript || display;
    this.setUserQuery("");
    this._chatStateService.addEntryToUsersQna(currentQnA);
    this._cdr.detectChanges();

    if (this.currentAgent === "VirtualAgent") {
      this.getResponseStreamAndUpdateQnA(currentQnA.id, false);
    } else {
      this.sendQueryToHumanAgent(this.currentUserQuery);
      this.saveHumanAgentMessageThread(this.currentUserQuery);
    }

    setTimeout(() => {
      this.scrollToBottom();
      this._cdr.detectChanges();
    }, 50);
  }

  /**
   * Generic widget submission handler. The rich-message dispatcher emits {type, response};
   * we forward to the backend via the component-interaction service and merge the returned
   * state into the local QnA so the widget updates immediately.
   */
  onWidgetSubmit(qnaId: string, payload: { type: string; response: Record<string, any> }) {
    const entry = this.userQna.find((qna) => qna.id === qnaId);
    const attachment = entry?.answer.attachment;
    if (!attachment) return;
    if (attachment.state?.["submitted"]) return;

    const contextInfoStr = this._sessionService.getContextInfo();
    const contextInfo = contextInfoStr ? JSON.parse(contextInfoStr) : {};
    const conversationContextId = contextInfo?.conversationContextId;
    if (!conversationContextId) return;

    this._componentInteractionService
      .submit(conversationContextId, attachment.messageId, payload.type, payload.response)
      .subscribe({
        next: (updated) => {
          this._chatStateService.updateAttachmentState(qnaId, updated.state ?? {});
          this._cdr.detectChanges();
          this.scrollToBottom();
        },
        error: (error) => {
          console.error("Failed to submit component response", payload.type, error);
        },
      });
  }

  get isSmsConversationChannelEnabled(): boolean {
    const raw =
      this.chatBotStyles?.smsConversationChannelEnabled ??
      this._configService.getCurrentConfig()?.smsConversationChannelEnabled;
    return raw === true || raw === "true";
  }

  get smsOffered(): boolean {
    if (this.smsChannelOption?.unavailableReason === ChatBubbleComponent.SMS_REASON_NOT_ACTIVATED) {
      return false;
    }
    if (this.smsChannelOption) {
      return true;
    }
    return this.smsOptionsLoaded ? false : this.isSmsConversationChannelEnabled;
  }

  get showSmsSwitchButton(): boolean {
    return !this.isConversationClosed
      && !this.smsSwitchCompleted
      && !this.smsSwitchPromptOpen
      && !!this.conversationContextId
      && this.smsOffered;
  }

  get smsPromptReady(): boolean {
    return this.smsSwitchPromptOpen && !this.smsSwitchResolving && this.smsOptionsLoaded;
  }

  get smsNeedsPhone(): boolean {
    if (!this.smsPromptReady) {
      return false;
    }
    if (!this.smsChannelOption) {
      return true;
    }
    if (this.smsChannelOption.available) {
      return false;
    }
    const reason = this.smsChannelOption.unavailableReason;
    return reason === ChatBubbleComponent.SMS_REASON_NO_MOBILE
      || reason === ChatBubbleComponent.SMS_REASON_INVALID_MOBILE;
  }

  get smsPromptHint(): string {
    if (!this.smsPromptReady) {
      return "";
    }
    if (this.smsChannelOption?.available) {
      return this.smsChannelOption.recordedMobile
        ? `We'll text ${this.smsChannelOption.recordedMobile}. Reply to that message to continue this chat.`
        : "We'll text you. Reply to that message to continue this chat.";
    }
    return "Enter your mobile number. We'll text you so you can continue this chat.";
  }

  get smsCanSubmit(): boolean {
    if (this.smsSwitchSubmitting || !this.smsPromptReady) {
      return false;
    }
    if (this.smsNeedsPhone) {
      return this.smsSwitchPhone.trim().length > 0;
    }
    return true;
  }

  private refreshSmsChannelOption(force = false): void {
    const conversationContextId = this.conversationContextId;
    if (!conversationContextId) {
      return;
    }
    if (!force && this.smsOptionsConversationId === conversationContextId) {
      return;
    }
    if (this.smsOptionsConversationId !== conversationContextId) {
      this.smsChannelOption = null;
      this.smsOptionsLoaded = false;
    }
    this.smsOptionsConversationId = conversationContextId;
    this.smsOptionsSub?.unsubscribe();
    this.smsOptionsSub = this._chatService.getChannelOptions(conversationContextId).pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe({
      next: (options) => {
        this.smsOptionsLoaded = true;
        this.smsChannelOption = (options || []).find((option) => option.channel === "SMS") ?? null;
        this.smsSwitchResolving = false;
        if (this.smsChannelOption?.unavailableReason === ChatBubbleComponent.SMS_REASON_NOT_ACTIVATED) {
          this.smsSwitchPromptOpen = false;
          this.smsSwitchError = null;
          this._cdr.detectChanges();
          return;
        }
        if (this.smsSwitchPromptOpen && this.smsNeedsPhone && !this.smsSwitchPhone.trim()) {
          this.smsSwitchPhone = this.smsChannelOption?.recordedMobile || "";
        }
        if (this.smsSwitchPromptOpen && this.smsNeedsPhone) {
          setTimeout(() => this.smsPhoneInput?.nativeElement?.focus());
        }
        this._cdr.detectChanges();
      },
      error: () => {
        this.smsChannelOption = null;
        this.smsOptionsLoaded = true;
        this.smsSwitchResolving = false;
      },
    });
  }

  openSmsSwitch(): void {
    if (!this.conversationContextId || !this.smsOffered || this.smsSwitchSubmitting) {
      return;
    }
    this.smsSwitchPromptOpen = true;
    this.smsSwitchResolving = true;
    this.smsSwitchError = null;
    this.smsSwitchPhone = "";
    this.pickerVisible = false;
    this.refreshSmsChannelOption(true);
  }

  cancelSmsSwitch(): void {
    this.smsSwitchPromptOpen = false;
    this.smsSwitchResolving = false;
    this.smsSwitchError = null;
    this.smsSwitchPhone = "";
    this.smsSwitchSubmitting = false;
  }

  onSmsPhoneInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.smsSwitchPhone = target?.value ?? "";
    this.smsSwitchError = null;
  }

  confirmSmsSwitch(): void {
    const conversationContextId = this.conversationContextId;
    if (!conversationContextId || this.smsSwitchSubmitting || !this.smsCanSubmit) {
      return;
    }
    this.smsSwitchSubmitting = true;
    this.smsSwitchError = null;
    this._chatService.switchChannel({
      conversationContextId,
      channel: "SMS",
      phoneNumber: this.smsSwitchPhone.trim() || undefined,
    }).pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe({
      next: (response) => {
        this.smsSwitchSubmitting = false;
        this.smsSwitchPromptOpen = false;
        this.smsSwitchCompleted = true;
        this.smsSwitchConfirmation = response?.confirmationMessage
          || "We've sent you a text. Reply to that message on your phone to continue.";
        this._cdr.detectChanges();
      },
      error: (error) => {
        this.smsSwitchSubmitting = false;
        this.smsSwitchError = this.customerSmsError(error);
        this._cdr.detectChanges();
      },
    });
  }

  /**
   * Customer kept typing in the widget after switching to SMS. Just clear the SMS banner —
   * do NOT call POST /chat/switch-channel here. That API's outbound-confirmation side effect
   * (HumanAgentService.customerSwitchChannel -> handler.send()) is meant to notify a customer on
   * a channel they're not currently looking at (e.g. push a real SMS). For Web Chat the widget
   * IS the client making this request, so that confirmation would loop back over the widget's own
   * STOMP subscription and get misread as a live human-agent message (chat-bubble's message
   * handler can't distinguish it from a real agent reply, and — worse — flips currentAgent to
   * "HumanAgent" if we were still on the bot, silently killing the rest of the bot conversation).
   * The channel bookkeeping is instead corrected server-side, without any outbound message, by
   * ChatController.chat() (VirtualAgent/SSE turns) and HumanAgentService.logHumanAgentInteraction
   * (human-agent turns) — both already tag/persist WEB_PORTAL whenever the widget sends a message.
   */
  private switchBackToWebChat(): void {
    this.smsSwitchCompleted = false;
    this.smsSwitchConfirmation = "";
    this.refreshSmsChannelOption(true);
  }

  private customerSmsError(error: unknown): string {
    const raw = this.extractErrorMessage(error);
    if (raw === ChatBubbleComponent.SMS_REASON_NO_MOBILE) {
      return "Enter a mobile number so we can text you.";
    }
    if (raw === ChatBubbleComponent.SMS_REASON_INVALID_MOBILE) {
      return "This doesn't look like a mobile number.";
    }
    if (raw === ChatBubbleComponent.SMS_REASON_NOT_ACTIVATED) {
      return "SMS isn't available right now.";
    }
    if (raw) {
      return raw;
    }
    return "Could not text this number. Try again.";
  }

  private extractErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const body = error.error;
      if (typeof body === "string") {
        return body;
      }
      if (body && typeof body === "object" && "message" in body) {
        const message = (body as { message?: unknown }).message;
        return typeof message === "string" ? message : "";
      }
      return error.message || "";
    }
    if (error instanceof Error) {
      return error.message;
    }
    return "";
  }
}
